import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { loadExternalScript, PUBLISHER_SCRIPT_URL } from "@/lib/third-party";

// Botón oficial "Agregar como fuente preferida en Google".
// El script oficial se carga al acercarse esta sección a la pantalla.
// Si Google no renderiza el botón (navegador no elegible, sin sesión, bloqueadores),
// mostramos un CTA propio que lleva a la configuración de fuentes preferidas.
const FALLBACK_URL =
  "https://www.google.com/preferences/source?q=tiemposhoy.com";

const GooglePreferredSource = () => {
  const slotRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [rendered, setRendered] = useState(false);

  const location =
    typeof window !== "undefined" ? window.location.pathname : "unknown";

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadExternalScript(PUBLISHER_SCRIPT_URL);
        observer.disconnect();
      }
    }, { rootMargin: "300px" });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const check = () => {
      const el = slotRef.current;
      if (!el) return false;
      const parent = el.parentElement;
      // Google puede inyectar un iframe/shadow, reemplazar el nodo o insertar
      // el botón como hermano del slot.
      const hasChildren = el.childElementCount > 0;
      const hasHeight = el.offsetHeight > 0 && el.offsetWidth > 0;
      const hasIframe = el.querySelector("iframe") !== null;
      const hasButton = el.querySelector("button") !== null;
      const replaced = !el.isConnected;
      const siblingButton =
        parent && parent.childElementCount > 1 && parent !== el.parentElement;
      if (
        hasChildren ||
        hasHeight ||
        hasIframe ||
        hasButton ||
        replaced ||
        siblingButton
      ) {
        setRendered(true);
        return true;
      }
      return false;
    };
    if (check()) return;

    const el = slotRef.current;
    if (!el) return;

    // Intervalo como respaldo.
    const id = window.setInterval(() => {
      if (check()) window.clearInterval(id);
    }, 500);
    const stop = window.setTimeout(() => window.clearInterval(id), 6000);

    // MutationObserver para detectar inyección del botón oficial de Google.
    let obs: MutationObserver | null = null;
    if (typeof MutationObserver !== "undefined") {
      obs = new MutationObserver(() => {
        if (check()) {
          window.clearInterval(id);
          window.clearTimeout(stop);
          obs?.disconnect();
        }
      });
      const parent = el.parentElement;
      if (parent) {
        obs.observe(parent, { childList: true, subtree: true });
      }
    }

    return () => {
      window.clearInterval(id);
      window.clearTimeout(stop);
      obs?.disconnect();
    };
  }, []);

  // Impresión: una sola vez por carga, cuando la tarjeta entra en pantalla.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let fired = false;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired) {
            fired = true;
            trackEvent("preferred_source_impression", { location });
            obs.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [location]);

  return (
    <section className="my-10" ref={sectionRef}>
      <div className="relative overflow-hidden rounded-3xl border border-border/50 bg-foreground shadow-xl">
        {/* Fondo de cuadrícula sutil */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "linear-gradient(to right, var(--color-background) 1px, transparent 1px), linear-gradient(to bottom, var(--color-background) 1px, transparent 1px)",
            backgroundSize: "2.5rem 2.5rem",
          }}
        />

        {/* Glows ambientales */}
        <div
          className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-primary/20 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="absolute -top-16 -left-16 h-56 w-56 rounded-full bg-costa-rica/15 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative z-10 p-6 md:p-10">
          {/* Indicador "en vivo" */}
          <div className="mb-4 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-background/60">
              Resultados en tiempo real
            </span>
          </div>

          <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
            <div className="flex-1">
              <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-background md:text-4xl">
                ¿Buscás los resultados{" "}
                <span className="bg-gradient-to-r from-highlight-start via-highlight-mid to-highlight-end bg-clip-text text-transparent">
                  primero que nadie
                </span>
                ?
              </h2>
              <p className="mt-2 max-w-xl text-base text-background/70 md:text-lg">
                Marcá <strong>Tiempos Hoy</strong> como tu fuente preferida en
                Google y nuestros resultados aparecerán de primeros en tus
                búsquedas. Toma 5 segundos y es gratis.
              </p>
            </div>

            <div className="flex min-h-12 max-w-full shrink-0 flex-col items-start gap-3 md:items-center">
              {/* Slot del botón oficial de Google */}
              <div
                ref={slotRef}
                google-add-preferred-source-btn=""
                data-lang="es"
                onClick={() =>
                  trackEvent("preferred_source_click", {
                    variant: "google_official",
                    location,
                  })
                }
              />

              {!rendered && (
                <Button
                  asChild
                  size="lg"
                  className="h-auto min-h-11 max-w-full whitespace-normal rounded-xl bg-background text-foreground shadow-lg hover:bg-background/90"
                >
                  <a
                    href={FALLBACK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("preferred_source_click", {
                        variant: "fallback",
                        location,
                      })
                    }
                  >
                    Agregar como fuente preferida
                    <ExternalLink
                      className="ml-2 h-4 w-4"
                      aria-hidden="true"
                    />
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GooglePreferredSource;
